import { micaStatsService, type MicaStats } from "./mica-stats.service"
import "./tbw-mica-charts.component.scss"

// ── Brand palette ────────────────────────────────────────────────────────────
const ROYAL = "#3442eb"
const NAVY = "#161032"
const MUTED = "#c8c5da"
// Ordered shade scale for the multi-slice sector donut.
const SECTOR_SCALE = ["#3442eb", "#4f48ee", "#6a64f1", "#8781f4", "#a8a3f7", "#2b2570", "#1f1a4d", NAVY]

type Segment = { label: string; sublabel?: string; value: number; color: string }

interface ChartCard {
  eyebrow: string
  title: string
  subtitle: string
  segments: Segment[]
  variant: "donut" | "pie"
  /** Index of the segment whose share is shown big in the donut centre (donut only). */
  centerIndex: number
  centerCaption: string
  footerNote: string
}

const SECTOR_TOP_N = 7

// ── i18n (charts copy) ───────────────────────────────────────────────────────
type Dict = Record<string, string>
const I18N: Record<string, Dict> = {
  en: {
    headline_label: "MiCA licences mapped", top_jurisdictions: "TOP JURISDICTIONS",
    geo_eyebrow: "GEOGRAPHY", geo_title: "EU-based vs. non-EU entities",
    geo_subtitle: "Where the {N} mapped entities are headquartered relative to the EU.",
    geo_eu: "EU / EEA-based", geo_eu_sub: "Home-grown European companies",
    geo_non: "Non-EU-based", geo_non_sub: "US, UK, Switzerland, Asia… passporting in",
    geo_center: "NON-EU",
    origin_eyebrow: "ORIGIN", origin_title: "Crypto-native vs. non crypto-native",
    origin_subtitle: "Split between crypto-first companies and incumbents (banks, fintechs, asset managers) entering the space.",
    origin_native: "Crypto-native", origin_native_sub: "Exchanges, custodians, DeFi, infra",
    origin_incumbent: "Incumbent / TradFi", origin_incumbent_sub: "Banks, brokers, neobanks, asset mgrs",
    origin_center: "NATIVE",
    sector_eyebrow: "ACTIVITY", sector_title: "Breakdown by sector",
    sector_subtitle: "Distribution of mapped entities across the main MiCA-regulated activities.",
    unknown: "Unknown", unclassified: "Unclassified", other: "Other",
    cost_eyebrow: "COST OF COMPLIANCE", cost_title: "Cost to obtain & maintain a MiCA licence",
    cost_subtitle: "Cost ranges are indicative and can vary significantly depending on the applicant’s level of regulatory maturity, existing compliance infrastructure, internal staffing capabilities, and choice of external legal and compliance advisors. Organisations that are already regulated and have established governance and compliance functions typically face substantially lower implementation costs than firms building these capabilities from scratch.",
    cost_max: "Max", cost_min: "Min",
    src_esma: "Source: ESMA MiCA register", src_tbw: "Source: The Big Whale estimates",
    cost_legal: "Regulatory & Legal Advisory", cost_hiring: "Internal Staffing & Governance",
    cost_compliance: "Compliance Implementation & Technology (incl. DORA)",
  },
  fr: {
    headline_label: "Licences MiCA cartographiées", top_jurisdictions: "PRINCIPALES JURIDICTIONS",
    geo_eyebrow: "GÉOGRAPHIE", geo_title: "Entités basées dans l’UE vs hors UE",
    geo_subtitle: "Où sont domiciliées les {N} entités cartographiées par rapport à l’UE.",
    geo_eu: "Basé UE / EEE", geo_eu_sub: "Entreprises européennes locales",
    geo_non: "Basé hors UE", geo_non_sub: "USA, UK, Suisse, Asie… en passporting",
    geo_center: "HORS UE",
    origin_eyebrow: "ORIGINE", origin_title: "Crypto-native vs non crypto-native",
    origin_subtitle: "Répartition entre entreprises crypto-natives et acteurs traditionnels (banques, fintechs, gérants d’actifs) qui entrent sur le marché.",
    origin_native: "Crypto-native", origin_native_sub: "Exchanges, dépositaires, DeFi, infra",
    origin_incumbent: "Acteur traditionnel / TradFi", origin_incumbent_sub: "Banques, courtiers, néobanques, gérants",
    origin_center: "NATIVE",
    sector_eyebrow: "ACTIVITÉ", sector_title: "Répartition par secteur",
    sector_subtitle: "Répartition des entités cartographiées selon les principales activités régulées par MiCA.",
    unknown: "Inconnu", unclassified: "Non classé", other: "Autres",
    cost_eyebrow: "COÛT DE LA CONFORMITÉ", cost_title: "Coût d’obtention et de maintien d’une licence MiCA",
    cost_subtitle: "Les fourchettes de coûts sont indicatives et peuvent varier fortement selon le niveau de maturité réglementaire du candidat, l’infrastructure de conformité existante, les capacités internes en personnel et le choix des conseils juridiques et conformité externes. Les organisations déjà régulées, dotées de fonctions de gouvernance et de conformité établies, supportent généralement des coûts de mise en œuvre nettement inférieurs à ceux des entreprises partant de zéro.",
    cost_max: "Max", cost_min: "Min",
    src_esma: "Source : registre MiCA de l’ESMA", src_tbw: "Source : estimations The Big Whale",
    cost_legal: "Conseil réglementaire & juridique", cost_hiring: "Personnel interne & gouvernance",
    cost_compliance: "Mise en conformité & technologie (incl. DORA)",
  },
  de: {
    headline_label: "Erfasste MiCA-Lizenzen", top_jurisdictions: "TOP-JURISDIKTIONEN",
    geo_eyebrow: "GEOGRAFIE", geo_title: "Unternehmen mit EU-Sitz vs. außerhalb der EU",
    geo_subtitle: "Wo die {N} erfassten Unternehmen ihren Sitz haben – im Verhältnis zur EU.",
    geo_eu: "EU/EWR-ansässig", geo_eu_sub: "Einheimische europäische Unternehmen",
    geo_non: "Außerhalb der EU ansässig", geo_non_sub: "USA, UK, Schweiz, Asien… mit EU-Pass",
    geo_center: "NICHT-EU",
    origin_eyebrow: "HERKUNFT", origin_title: "Krypto-nativ vs. nicht krypto-nativ",
    origin_subtitle: "Aufteilung zwischen krypto-nativen Unternehmen und etablierten Akteuren (Banken, Fintechs, Vermögensverwalter), die in den Markt eintreten.",
    origin_native: "Krypto-nativ", origin_native_sub: "Börsen, Verwahrer, DeFi, Infrastruktur",
    origin_incumbent: "Etabliert / TradFi", origin_incumbent_sub: "Banken, Broker, Neobanken, Verwalter",
    origin_center: "NATIV",
    sector_eyebrow: "AKTIVITÄT", sector_title: "Aufschlüsselung nach Sektor",
    sector_subtitle: "Verteilung der erfassten Unternehmen auf die wichtigsten MiCA-regulierten Aktivitäten.",
    unknown: "Unbekannt", unclassified: "Nicht klassifiziert", other: "Sonstige",
    cost_eyebrow: "COMPLIANCE-KOSTEN", cost_title: "Kosten für Erlangung & Aufrechterhaltung einer MiCA-Lizenz",
    cost_subtitle: "Die Kostenspannen sind Richtwerte und können je nach regulatorischer Reife des Antragstellers, bestehender Compliance-Infrastruktur, internen Personalkapazitäten und der Wahl externer Rechts- und Compliance-Berater erheblich variieren. Bereits regulierte Organisationen mit etablierten Governance- und Compliance-Funktionen haben in der Regel deutlich niedrigere Umsetzungskosten als Firmen, die diese Kapazitäten von Grund auf aufbauen.",
    cost_max: "Max", cost_min: "Min",
    src_esma: "Quelle: ESMA MiCA-Register", src_tbw: "Quelle: Schätzungen von The Big Whale",
    cost_legal: "Regulatorische & rechtliche Beratung", cost_hiring: "Internes Personal & Governance",
    cost_compliance: "Compliance-Umsetzung & Technologie (inkl. DORA)",
  },
  nl: {
    headline_label: "In kaart gebrachte MiCA-vergunningen", top_jurisdictions: "TOP-RECHTSGEBIEDEN",
    geo_eyebrow: "GEOGRAFIE", geo_title: "In de EU gevestigd vs. buiten de EU",
    geo_subtitle: "Waar de {N} in kaart gebrachte entiteiten gevestigd zijn ten opzichte van de EU.",
    geo_eu: "Gevestigd in EU/EER", geo_eu_sub: "Europese bedrijven van eigen bodem",
    geo_non: "Buiten de EU gevestigd", geo_non_sub: "VS, VK, Zwitserland, Azië… via passporting",
    geo_center: "NIET-EU",
    origin_eyebrow: "OORSPRONG", origin_title: "Crypto-native vs. niet-crypto-native",
    origin_subtitle: "Verdeling tussen crypto-native bedrijven en gevestigde partijen (banken, fintechs, vermogensbeheerders) die de markt betreden.",
    origin_native: "Crypto-native", origin_native_sub: "Exchanges, custodians, DeFi, infra",
    origin_incumbent: "Gevestigd / TradFi", origin_incumbent_sub: "Banken, brokers, neobanken, beheerders",
    origin_center: "NATIVE",
    sector_eyebrow: "ACTIVITEIT", sector_title: "Uitsplitsing per sector",
    sector_subtitle: "Verdeling van de in kaart gebrachte entiteiten over de belangrijkste MiCA-gereguleerde activiteiten.",
    unknown: "Onbekend", unclassified: "Niet geclassificeerd", other: "Overige",
    cost_eyebrow: "NALEVINGSKOSTEN", cost_title: "Kosten voor verkrijgen & behouden van een MiCA-vergunning",
    cost_subtitle: "De kostenmarges zijn indicatief en kunnen sterk variëren afhankelijk van de regulatoire volwassenheid van de aanvrager, de bestaande compliance-infrastructuur, de interne personeelscapaciteit en de keuze van externe juridische en compliance-adviseurs. Organisaties die al gereguleerd zijn en over gevestigde governance- en compliancefuncties beschikken, hebben doorgaans aanzienlijk lagere implementatiekosten dan bedrijven die deze capaciteiten vanaf nul opbouwen.",
    cost_max: "Max", cost_min: "Min",
    src_esma: "Bron: ESMA MiCA-register", src_tbw: "Bron: schattingen van The Big Whale",
    cost_legal: "Regelgevend & juridisch advies", cost_hiring: "Intern personeel & governance",
    cost_compliance: "Compliance-implementatie & technologie (incl. DORA)",
  },
}
function pickLang(): string {
  const l = (document.documentElement.lang || "en").slice(0, 2).toLowerCase()
  return l in I18N ? l : "en"
}
const T: Dict = I18N[pickLang()]
const COST_LABEL_BY_KEY: Record<string, string> = {
  legal: T.cost_legal, hiring: T.cost_hiring, compliance: T.cost_compliance,
}

export class TbwMicaChartsComponent extends HTMLElement {
  private get chartKind(): string {
    return (this.getAttribute("chart") || "all").toLowerCase()
  }
  private get type(): string {
    return (this.getAttribute("type") || "casp").toLowerCase()
  }

  async connectedCallback() {
    this.classList.add("tbw-mica")
    this.innerHTML = `<div class="tbw-mica__loading">Loading…</div>`
    try {
      const stats = await micaStatsService.get(this.type)
      this.renderCards(stats)
    } catch {
      this.innerHTML = `<div class="tbw-mica__error">Unable to load the MiCA data right now.</div>`
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  private renderCards(stats: MicaStats) {
    if (this.chartKind === "headline") {
      this.innerHTML = this.headlineHtml(stats)
      this.animateCount()
      return
    }
    if (this.chartKind === "all") {
      // 2×2 on desktop, single column on mobile (handled in CSS).
      const cards = ["sector", "origin", "cost", "geography"].map((k) => this.cardHtml(k, stats)).join("")
      this.innerHTML = `<div class="tbw-mica__grid">${cards}</div>`
    } else {
      this.innerHTML = this.cardHtml(this.chartKind, stats)
    }
    this.wireInteractions()
  }

  // ── Headline: big live counter + top jurisdictions ───────────────────────────
  private headlineHtml(stats: MicaStats): string {
    const total = stats.totalEntities
    const top = [...(stats.topCountries ?? [])].slice(0, 5)
    const max = top.length ? top[0].count : 1
    const rows = top
      .map(
        (c) => `
        <li class="tbw-mica__hl-row">
          <span class="tbw-mica__hl-country">${c.name}</span>
          <span class="tbw-mica__hl-track"><span class="tbw-mica__hl-bar" style="width:${Math.max(
            8,
            Math.round((c.count / max) * 100)
          )}%"></span></span>
          <span class="tbw-mica__hl-count">${c.count}</span>
        </li>`
      )
      .join("")
    return `
      <div class="tbw-mica__headline">
        <div class="tbw-mica__hl-stat">
          <span class="tbw-mica__hl-num" data-count="${total}">0</span>
          <span class="tbw-mica__hl-label">${T.headline_label}</span>
        </div>
        <div class="tbw-mica__hl-top">
          <p class="tbw-mica__eyebrow">${T.top_jurisdictions}</p>
          <ul class="tbw-mica__hl-list">${rows}</ul>
        </div>
      </div>`
  }

  /** Count up to the target the first time the headline scrolls into view. */
  private animateCount() {
    const el = this.querySelector<HTMLElement>(".tbw-mica__hl-num")
    if (!el) return
    const target = Number(el.getAttribute("data-count") || "0")
    const run = () => {
      const dur = 1200
      let start = 0
      const step = (ts: number) => {
        if (!start) start = ts
        const p = Math.min(1, (ts - start) / dur)
        const eased = 1 - Math.pow(1 - p, 3)
        el.textContent = String(Math.round(eased * target))
        if (p < 1) requestAnimationFrame(step)
        else el.textContent = String(target)
      }
      requestAnimationFrame(step)
    }
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries, obs) => {
          for (const e of entries)
            if (e.isIntersecting) {
              run()
              obs.disconnect()
              break
            }
        },
        { threshold: 0.4 }
      )
      io.observe(el)
    } else {
      run()
    }
  }

  private cardHtml(kind: string, stats: MicaStats): string {
    if (kind === "cost") return this.costCardHtml(stats)
    const card = this.buildDonutCard(kind, stats)
    if (!card) return ""
    return this.donutCardHtml(card, stats)
  }

  private brandHeader(eyebrow: string, title: string, subtitle: string): string {
    return `
      <div class="tbw-mica__head">
        <p class="tbw-mica__eyebrow">${eyebrow}</p>
        <h3 class="tbw-mica__title">${title}</h3>
        <p class="tbw-mica__subtitle">${subtitle}</p>
      </div>`
  }

  private footer(note: string): string {
    return `
      <div class="tbw-mica__footer">
        <span>${note}</span>
      </div>`
  }

  // ── Donut charts (geography / origin / sector) ───────────────────────────────
  private buildDonutCard(kind: string, stats: MicaStats): ChartCard | null {
    const footerNote = T.src_esma

    if (kind === "geography") {
      const g = stats.geography
      const segments: Segment[] = [
        { label: T.geo_eu, sublabel: T.geo_eu_sub, value: g.euBased, color: ROYAL },
        { label: T.geo_non, sublabel: T.geo_non_sub, value: g.nonEu, color: NAVY },
      ]
      if (g.unknown > 0) segments.push({ label: T.unknown, value: g.unknown, color: MUTED })
      return {
        eyebrow: T.geo_eyebrow,
        title: T.geo_title,
        subtitle: T.geo_subtitle.replace("{N}", this.fmt(stats.totalEntities)),
        segments,
        variant: "donut",
        centerIndex: 1,
        centerCaption: T.geo_center,
        footerNote,
      }
    }

    if (kind === "origin") {
      const o = stats.origin
      const segments: Segment[] = [
        { label: T.origin_native, sublabel: T.origin_native_sub, value: o.cryptoNative, color: ROYAL },
        { label: T.origin_incumbent, sublabel: T.origin_incumbent_sub, value: o.incumbent, color: NAVY },
      ]
      if (o.unknown > 0) segments.push({ label: T.unclassified, value: o.unknown, color: MUTED })
      return {
        eyebrow: T.origin_eyebrow,
        title: T.origin_title,
        subtitle: T.origin_subtitle,
        segments,
        variant: "donut",
        centerIndex: 0,
        centerCaption: T.origin_center,
        footerNote,
      }
    }

    if (kind === "sector") {
      const segments = this.sectorSegments(stats.sectors)
      return {
        eyebrow: T.sector_eyebrow,
        title: T.sector_title,
        subtitle: T.sector_subtitle,
        segments,
        variant: "pie",
        centerIndex: 0,
        centerCaption: "",
        footerNote,
      }
    }

    return null
  }

  /** Top-N sectors + an aggregated "Other" tail. */
  private sectorSegments(sectors: Array<{ name: string; count: number }>): Segment[] {
    const sorted = [...sectors].sort((a, b) => b.count - a.count)
    const head = sorted.slice(0, SECTOR_TOP_N)
    const tail = sorted.slice(SECTOR_TOP_N)
    const segments: Segment[] = head.map((s, i) => ({
      label: s.name,
      value: s.count,
      color: SECTOR_SCALE[i % SECTOR_SCALE.length],
    }))
    const otherTotal = tail.reduce((sum, s) => sum + s.count, 0)
    if (otherTotal > 0) {
      segments.push({
        label: T.other,
        sublabel: tail.slice(0, 3).map((s) => s.name).join(" · "),
        value: otherTotal,
        color: NAVY,
      })
    }
    return segments
  }

  private donutCardHtml(card: ChartCard, _stats: MicaStats): string {
    const total = card.segments.reduce((s, x) => s + x.value, 0) || 1
    const centerSeg = card.segments[card.centerIndex] ?? card.segments[0]
    const centerPct = Math.round((centerSeg.value / total) * 100)

    const chartHtml =
      card.variant === "pie"
        ? `<div class="tbw-mica__chart-wrap">${this.pieSvg(card.segments, total)}</div>`
        : `<div class="tbw-mica__chart-wrap">
            ${this.donutSvg(card.segments, total)}
            <div class="tbw-mica__donut-center">
              <span class="tbw-mica__center-pct">${centerPct}%</span>
              <span class="tbw-mica__center-cap">${card.centerCaption}</span>
            </div>
          </div>`

    return `
      <article class="tbw-mica__card" data-total="${total}">
        ${this.brandHeader(card.eyebrow, card.title, card.subtitle)}
        <div class="tbw-mica__body">
          ${chartHtml}
          <ul class="tbw-mica__legend">
            ${card.segments
              .map((seg, i) => {
                const pct = Math.round((seg.value / total) * 100)
                return `
                <li class="tbw-mica__legend-row" data-index="${i}">
                  <span class="tbw-mica__swatch" style="background:${seg.color}"></span>
                  <span class="tbw-mica__legend-label">
                    <strong>${seg.label}</strong>
                    ${seg.sublabel ? `<em>${seg.sublabel}</em>` : ""}
                  </span>
                  <span class="tbw-mica__legend-count">${this.fmt(seg.value)}</span>
                  <span class="tbw-mica__legend-pct">${pct}%</span>
                </li>`
              })
              .join("")}
          </ul>
        </div>
        ${this.footer(card.footerNote)}
        <div class="tbw-mica__tooltip" hidden></div>
      </article>`
  }

  /** Stacked-arc donut. pathLength=100 lets us drive dash with raw percentages. */
  private donutSvg(segments: Segment[], total: number): string {
    const GAP = 0.7 // % of circumference left white between slices
    let offset = 0
    const arcs = segments
      .map((seg, i) => {
        const pct = (seg.value / total) * 100
        const dash = Math.max(0, pct - GAP)
        const circle = `
          <circle class="tbw-mica__seg tbw-mica__arc" data-index="${i}"
            cx="100" cy="100" r="70" fill="none"
            stroke="${seg.color}" stroke-width="26" pathLength="100"
            stroke-dasharray="${dash} ${100 - dash}"
            stroke-dashoffset="${-offset}" />`
        offset += pct
        return circle
      })
      .join("")
    return `<svg class="tbw-mica__svg tbw-mica__donut" viewBox="0 0 200 200" role="img">
      <g transform="rotate(-90 100 100)">${arcs}</g>
    </svg>`
  }

  /** Full pie chart (filled wedges) — used for the sector breakdown. */
  private pieSvg(segments: Segment[], total: number): string {
    const cx = 100
    const cy = 100
    const r = 96
    const point = (angleDeg: number) => {
      const a = ((angleDeg - 90) * Math.PI) / 180 // 0% starts at top
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
    }
    let acc = 0
    const wedges = segments
      .map((seg, i) => {
        const startAngle = (acc / total) * 360
        acc += seg.value
        const endAngle = (acc / total) * 360
        const [x0, y0] = point(startAngle)
        const [x1, y1] = point(endAngle)
        const largeArc = endAngle - startAngle > 180 ? 1 : 0
        const d = `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`
        return `<path class="tbw-mica__seg tbw-mica__wedge" data-index="${i}" d="${d}" fill="${seg.color}" />`
      })
      .join("")
    return `<svg class="tbw-mica__svg tbw-mica__pie" viewBox="0 0 200 200" role="img">${wedges}</svg>`
  }

  // ── Cost bar chart (static editorial ranges) ─────────────────────────────────
  private costCardHtml(stats: MicaStats): string {
    const items = stats.cost.items
    const AXIS_MAX = 400_000
    const ticks = [0, 100_000, 200_000, 300_000, 400_000]
    const H = 320 // plot height in svg units
    const y = (v: number) => H - (v / AXIS_MAX) * H

    const grid = ticks
      .map((t) => `<g><line x1="0" x2="900" y1="${y(t)}" y2="${y(t)}" class="tbw-mica__gridline"/>
        <text x="-12" y="${y(t) + 4}" class="tbw-mica__axis">€${t / 1000}k</text></g>`)
      .join("")

    const colW = 900 / items.length
    const barW = 150
    const bars = items
      .map((it, i) => {
        const cx = colW * i + colW / 2
        const topY = y(it.max)
        const botY = y(it.min)
        return `
        <g class="tbw-mica__cost-col" transform="translate(${cx - barW / 2},0)">
          <rect class="tbw-mica__bar" x="0" y="${topY}" width="${barW}" height="${botY - topY}" rx="14"/>
          <text class="tbw-mica__bar-max" x="${barW / 2}" y="${topY - 14}">${T.cost_max} : €${Math.round(it.max / 1000)}k</text>
          <text class="tbw-mica__bar-min" x="${barW / 2}" y="${botY + 26}">${T.cost_min} : €${Math.round(it.min / 1000)}k</text>
          <text class="tbw-mica__bar-label" x="${barW / 2}" y="${H + 42}">${this.wrapTspans(COST_LABEL_BY_KEY[it.key] ?? it.label, barW / 2)}</text>
        </g>`
      })
      .join("")

    return `
      <article class="tbw-mica__card">
        ${this.brandHeader(T.cost_eyebrow, T.cost_title, T.cost_subtitle)}
        <div class="tbw-mica__cost-plot">
          <svg viewBox="-60 -30 990 470" role="img">
            ${grid}
            ${bars}
          </svg>
        </div>
        ${this.footer(T.src_tbw)}
      </article>`
  }

  /** Greedy word-wrap into centered <tspan> lines for SVG bar labels. */
  private wrapTspans(label: string, x: number, maxChars = 24): string {
    const words = label.split(" ")
    const lines: string[] = []
    let cur = ""
    for (const w of words) {
      if (!cur) cur = w
      else if ((cur + " " + w).length <= maxChars) cur += " " + w
      else { lines.push(cur); cur = w }
    }
    if (cur) lines.push(cur)
    return lines.map((ln, i) => `<tspan x="${x}" dy="${i === 0 ? 0 : 18}">${ln}</tspan>`).join("")
  }

  // ── Interactivity: hover sync between arcs, legend rows and the tooltip ───────
  private wireInteractions() {
    this.querySelectorAll<HTMLElement>(".tbw-mica__card").forEach((card) => {
      const tooltip = card.querySelector<HTMLElement>(".tbw-mica__tooltip")
      const segs = Array.from(card.querySelectorAll<SVGElement>(".tbw-mica__seg"))
      const rows = Array.from(card.querySelectorAll<HTMLElement>(".tbw-mica__legend-row"))
      if (!segs.length) return
      const total = Number(card.dataset.total || "0") || 1

      const setActive = (idx: number | null) => {
        segs.forEach((s) => s.classList.toggle("is-active", Number((s as any).dataset.index) === idx))
        rows.forEach((r) => r.classList.toggle("is-active", Number(r.dataset.index) === idx))
      }

      const showTip = (idx: number, clientX: number, clientY: number) => {
        const row = rows[idx]
        if (!row || !tooltip) return
        const label = row.querySelector(".tbw-mica__legend-label strong")?.textContent ?? ""
        const count = row.querySelector(".tbw-mica__legend-count")?.textContent ?? ""
        const pct = Math.round((Number(count.replace(/\D/g, "")) / total) * 100)
        tooltip.innerHTML = `<strong>${label}</strong> ${count} <span>(${pct}%)</span>`
        tooltip.hidden = false
        const rect = card.getBoundingClientRect()
        tooltip.style.left = `${clientX - rect.left}px`
        tooltip.style.top = `${clientY - rect.top}px`
      }
      const hideTip = () => {
        if (tooltip) tooltip.hidden = true
        setActive(null)
      }

      const bind = (el: HTMLElement | SVGElement, idx: number) => {
        el.addEventListener("mouseenter", (e) => {
          setActive(idx)
          showTip(idx, (e as MouseEvent).clientX, (e as MouseEvent).clientY)
        })
        el.addEventListener("mousemove", (e) => showTip(idx, (e as MouseEvent).clientX, (e as MouseEvent).clientY))
        el.addEventListener("mouseleave", hideTip)
      }
      segs.forEach((s) => bind(s, Number((s as any).dataset.index)))
      rows.forEach((r) => bind(r, Number(r.dataset.index)))
    })
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  private fmt(n: number): string {
    return new Intl.NumberFormat("en-US").format(n)
  }

}
